import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import Modal from 'react-modal';
import { useQuery } from 'react-apollo';
import { useHistory, useRouteMatch } from 'react-router-dom';
import qs from 'query-string';

import Button from '../../../../../../components/global/Button';
import Angle from '../../../../../../components/global/Angle';
import { getAsset } from '../../../../../../util/assets';
import getConfig from '../../../../../../util/config';
import useVideoSlideshowPlayback from '../../../../../../components/grid/components/VideoSlideshowGridViewer/hooks/useVideoSlideshowPlayback';
import sleep from '../../../../../../util/sleep';
import PlayButtonIcon from '../../../../../../components/icons/PlayButtonIcon';
import PauseButtonIcon from '../../../../../../components/icons/PauseButtonIcon';
import FullScreenLoader from '../../../../../../components/global/FullScreenLoader';
import { AlbumWithGreetingVideo } from '../../../../../../graphql/queries/albums';
import {
  isNativeAppWebView,
  postNativeAppAlbumSlideshowViewerDismiss,
} from '../../../../../../services/native-bridge/native-apps-interface';

const SlideshowGreetingView = () => {
  const match = useRouteMatch();
  const history = useHistory();
  const isNativeView = isNativeAppWebView();
  const { albumId } = match?.params;
  const { data: { album } = {} } = useQuery(AlbumWithGreetingVideo, {
    variables: {
      id: albumId,
    },
    skip: !albumId,
  });
  const [hideMenus, setHideMenus] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [flipVideo, setFlipVideo] = useState(false);
  const hideMenusTimer = useRef();
  const showControlsTimer = useRef();
  const config = getConfig();

  const onMouseMove = () => {
    setHideMenus(false);
    if (hideMenusTimer.current) {
      clearTimeout(hideMenusTimer.current);
      hideMenusTimer.current = null;
    }

    hideMenusTimer.current = setTimeout(() => setHideMenus(true), 2000);
  };

  const { url } = match;
  const basePath = url.split('/').slice(0, -1).join('/');

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.focus();
    // Trigger a mouse move. This ensures that the menu will be
    // hiding even if the user doesn't move the mouse at all.
    onMouseMove();

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      //  Clear the timeout if it exists by this point
      if (hideMenusTimer.current) {
        clearTimeout(hideMenusTimer.current);
        hideMenusTimer.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (showControls) {
      if (showControlsTimer.current) {
        clearTimeout(showControlsTimer.current);
        showControlsTimer.current = null;
      }

      showControlsTimer.current = setTimeout(
        () => setShowControls(false),
        2000,
      );
    }
    return () => {
      if (showControlsTimer.current) {
        clearTimeout(showControlsTimer.current);
        showControlsTimer.current = null;
      }
    };
  }, [showControls]);

  const modalClasses = classNames({
    'slideshow-grid-viewer': true,
    'hide-until-move': hideMenus,
  });

  const headerClasses = classNames({
    'slideshow-grid-viewer-header': true,
    'hide-until-move': hideMenus,
  });

  const bodyClasses = classNames({
    'slideshow-grid-viewer-body': true,
    'full-until-move': hideMenus,
  });

  /**
   * Dismisses the Slide Viewer
   */
  const onDismissVideoSlideshow = () => {
    if (isNativeView) {
      postNativeAppAlbumSlideshowViewerDismiss();
    } else {
      history.goBack();
    }
  };

  const onChangeVideo = async () => {
    setFlipVideo(true);
    await sleep(1000);
    history.replace(`${basePath}/solicitation`);
  };

  const greetingVideo =
    album?.greeting_video?.records?.[0] ??
    album?.slideshow?.default_greeting_video;

  const {
    isPlaying,
    onPlaybackStart,
    onPlaybackStop,
    elapsedPercent,
    videoPlayerRef,
  } = useVideoSlideshowPlayback({
    videos: [greetingVideo],
    video: greetingVideo,
    onChangeVideo,
    onPlayVideo: async () => {
      await sleep(2500);
      setFlipVideo(false);
    },
  });

  const controlsClasses = classNames({
    'slideshow-grid-viewer-video-menu-buttons': true,
    visible: showControls || !isPlaying,
  });

  const animatableClasses = classNames({
    'slideshow-grid-viewer-animatable': true,
    flipped: flipVideo || elapsedPercent > 0.95,
  });

  if (!album || !greetingVideo) {
    return <FullScreenLoader />;
  }

  const greetingVideoUrl = `${greetingVideo?.embed_url}&${qs.stringify({
    badge: 0,
    pip: 0,
    controls: 0,
    playsinline: 1,
    autoplay: 1,
  })}`;

  return (
    <Modal
      ariaHideApp={false}
      isOpen
      bodyOpenClassName="body-modal-open"
      overlayClassName="slideshow-grid-viewer-overlay"
      className={modalClasses}
    >
      {config?.marcomUrl && (
        <a
          className="slideshow-grid-viewer-waldo-logo"
          href={config.marcomUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src={getAsset(`/powered-by-waldo.svg`)} alt="Waldo Logo" />
        </a>
      )}
      <Button
        btnStyle="reset"
        onClick={() => {
          onPlaybackStop();
          onDismissVideoSlideshow();
        }}
        className="video-slideshow-btn-dismiss text-lg"
      >
        <Angle direction="left" />
      </Button>
      <div className={headerClasses}>
        <div className="slideshow-grid-viewer-header-left" />

        <div className="slideshow-grid-viewer-header-center">
          <h3 className="w-100 text-white text-center mb-0">{album.name}</h3>
        </div>
        <div className="slideshow-grid-viewer-header-right" />
      </div>
      <div className={bodyClasses}>
        <div className="slideshow-grid-viewer-content">
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
          <div
            role="button"
            tabIndex={0}
            className="slideshow-grid-viewer-video-menu"
            onClick={() => setShowControls(true)}
          >
            <div className={controlsClasses}>
              <Button
                btnStyle="reset"
                disabled={flipVideo || elapsedPercent > 0.95}
                onClick={() => {
                  if (isPlaying) {
                    onPlaybackStop();
                  } else {
                    onPlaybackStart();
                  }
                }}
              >
                {isPlaying ? (
                  <PauseButtonIcon size={64} color="#fff" />
                ) : (
                  <PlayButtonIcon size={64} color="#fff" />
                )}
              </Button>
            </div>
          </div>
          {/* Actively changing the src on the iframe impedes proper function from vimeo. */}
          <div className={animatableClasses}>
            <div className="slideshow-grid-viewer-flip-card">
              <iframe
                className={classNames({
                  'slideshow-grid-viewer-flip-card-front': true,
                })}
                title={greetingVideo?.name}
                src={greetingVideoUrl}
                ref={videoPlayerRef}
                playsInline="1"
                allow="autoplay"
                allowFullScreen={false}
                webkitallowfullscreen={false}
                mozallowfullscreen={false}
              />
              <div className="slideshow-grid-viewer-flip-card-back" />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SlideshowGreetingView;
